import { Buffer } from 'buffer';
import { jwtDecode } from 'jwt-decode';
import { Api } from '../api';
import { AuthProvider, AuthProviderState, AuthState, AmpAccessToken } from './Auth';
import { SessionKeys } from '../types';

const redirectUri = `${window.location.protocol}//${window.location.host}`;

enum JwtTokenPayloadKeys {
  preferredUsername = 'preferred_username',
  email = 'email',
  groups = 'custom:tsp:groups',
  tenantId = 'custom:tsp:tenant_id',
}

interface JwtTokenPayload {
  [JwtTokenPayloadKeys.preferredUsername]: string;
  [JwtTokenPayloadKeys.email]: string;
  [JwtTokenPayloadKeys.groups]: string;
  [JwtTokenPayloadKeys.tenantId]: string;
}

export interface AmpTokenValues {
  preferredUsername: string;
  email: string;
  groups: string;
  tenantId: string;
}

const getTokenValues = (token: string): AmpTokenValues => {
  const decodedPayload: JwtTokenPayload = jwtDecode(token);
  const tokenValues = {
    preferredUsername: decodedPayload[JwtTokenPayloadKeys.preferredUsername],
    email: decodedPayload[JwtTokenPayloadKeys.email],
    groups: decodedPayload[JwtTokenPayloadKeys.groups],
    tenantId: decodedPayload[JwtTokenPayloadKeys.tenantId],
  } as AmpTokenValues;
  return tokenValues;
};

const getUsernameForDisplay = (token: string): string => {
  const { email, preferredUsername } = getTokenValues(token);
  return preferredUsername.indexOf('@') > -1 ? preferredUsername : email;
};

const encodeKeyValue = (key: string, value: string): string => {
  return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

const base64URLEncode = (str: ArrayBuffer) => {
  return Buffer.from(str).toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const sha256 = (buffer: BufferSource) => {
  return crypto.subtle.digest('SHA-256', buffer);
};

const newCode = async (): Promise<string[]> => {
  const verifierBytes = new Int8Array(32);
  crypto.getRandomValues(verifierBytes);
  const verifier = base64URLEncode(verifierBytes);
  const challenge = base64URLEncode(await sha256(Buffer.from(verifier)));
  return [verifier, challenge];
};

const getCodeFromQueryParams = (): string | null => {
  const { search } = window.location;
  const params = new URLSearchParams(search);
  const code = params.get('code');
  if (!code) {
    return null;
  }
  return code;
};

const buildAuthUrl = (b: string, clientId: string, codeChallenge: string, username: string, isSSO: boolean): string => {
  const query = [
    encodeKeyValue('client_id', clientId),
    encodeKeyValue('redirect_uri', redirectUri),
    encodeKeyValue('response_type', 'code'),
    encodeKeyValue('scope', 'openid'),
    encodeKeyValue('code_challenge', codeChallenge),
    encodeKeyValue('code_challenge_method', 'S256'),
    isSSO ? encodeKeyValue('email', username) : encodeKeyValue('username', username),
  ].join('&');
  return `${b}?${query}`;
};

export interface AuthStorage {
  authEndpoint: string;
  tokenEndpoint: string;
  clientId: string;
  oidcMetadataUrl: string;
}

export interface OidcConfigStorage {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint: string;
}

export interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

async function getOidcToken(tokenUrl: string, body: string): Promise<AmpAccessToken> {
  const res = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });
  if (res.status !== 200) {
    const err = (await res.json()) as object;
    throw new Error(`Couldn't get token: ${JSON.stringify(err)}`);
  }

  const tokenResponse = (await res.json()) as TokenResponse;

  sessionStorage.setItem(SessionKeys.AmpToken, tokenResponse.access_token);
  sessionStorage.setItem(SessionKeys.AmpIdToken, tokenResponse.id_token);
  sessionStorage.setItem(SessionKeys.AmpRefreshToken, tokenResponse.refresh_token);
  const expiration = new Date(new Date().getTime() + tokenResponse.expires_in * 1000);
  sessionStorage.setItem(SessionKeys.AmpTokenExpiration, expiration.toISOString());

  return {
    token: tokenResponse.access_token,
    expiration,
  };
}

function getTokenByRefresh(tokenUrl: string, clientId: string, refreshToken: string): Promise<AmpAccessToken> {
  const body = [
    encodeKeyValue('client_id', clientId),
    encodeKeyValue('grant_type', 'refresh_token'),
    encodeKeyValue('refresh_token', refreshToken),
  ].join('&');
  return getOidcToken(tokenUrl, body);
}

function getInitialToken(
  tokenUrl: string,
  clientId: string,
  code: string,
  codeVerifier: string
): Promise<AmpAccessToken> {
  const body = [
    encodeKeyValue('client_id', clientId),
    encodeKeyValue('redirect_uri', redirectUri),
    encodeKeyValue('code', code),
    encodeKeyValue('grant_type', 'authorization_code'),
    encodeKeyValue('code_verifier', codeVerifier),
  ].join('&');
  return getOidcToken(tokenUrl, body);
}

export function mustGetSessionStorage(key: string): string {
  const value = sessionStorage.getItem(key);
  if (value === null) {
    throw new Error(`key not found in session storage: ${key}`);
  }

  return value;
}

export const newOidcProvider = (): AuthProvider => {
  async function getOidcInfo(oauthMetaUrl: string) {
    return fetch(oauthMetaUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
      .then(async (res) => {
        if (res.status !== 200) {
          const err = (await res.json()) as object;
          throw new Error(`Couldn't get openid config: ${JSON.stringify(err)}`);
        }
        return res;
      })
      .then((res) => res.json() as Promise<OidcConfigStorage>);
  }

  const getInitialAuthState = async (): Promise<AuthProviderState> => {
    const token = sessionStorage.getItem(SessionKeys.AmpToken);
    const verifier = sessionStorage.getItem(SessionKeys.Verifier);
    const authStorage = sessionStorage.getItem(SessionKeys.Auth);
    const oidcMeta = sessionStorage.getItem(SessionKeys.OidcMeta);
    const auth: AuthStorage | null = authStorage !== null ? (JSON.parse(authStorage) as AuthStorage) : null;
    const code = getCodeFromQueryParams();

    if (token !== null) {
      const username = getUsernameForDisplay(token);
      return Promise.resolve({
        authState: AuthState.SIGNED_IN,
        user: username,
        session: { username },
      });
    }

    if (auth === null || code === null || verifier === null) {
      return Promise.resolve({
        authState: AuthState.SIGN_IN,
        user: undefined,
        session: { username: '' },
      });
    }

    if (oidcMeta === null) {
      const oauthMetadata = await getOidcInfo(auth.oidcMetadataUrl);
      sessionStorage.setItem(SessionKeys.OidcMeta, JSON.stringify(oauthMetadata));
    }

    let tokenResponse: AmpAccessToken;
    try {
      tokenResponse = await getInitialToken(auth.tokenEndpoint, auth.clientId, code, verifier);
    } catch (err) {
      return Promise.resolve({
        authState: AuthState.SIGN_IN,
        user: undefined,
        session: { username: '' },
      });
    }

    const username = getUsernameForDisplay(tokenResponse.token);
    return Promise.resolve({
      authState: AuthState.SIGNED_IN,
      user: username,
      session: { username },
    });
  };

  const signIn = async (username: string) => {
    const oidcMetadataResponse = await Api.auth.getOidcMetadata({ username });

    const [v, challenge] = await newCode();
    sessionStorage.setItem(SessionKeys.Auth, JSON.stringify(oidcMetadataResponse));
    sessionStorage.setItem(SessionKeys.Verifier, v);
    const authUrl = buildAuthUrl(
      oidcMetadataResponse.authEndpoint,
      oidcMetadataResponse.clientId,
      challenge,
      username,
      false
    );
    window.location.replace(authUrl);
  };

  const signInIdp = async (email: string) => {
    const oidcMetadataResponse = await Api.auth.getOidcMetadata({ email });

    const [v, challenge] = await newCode();
    sessionStorage.setItem(SessionKeys.Auth, JSON.stringify(oidcMetadataResponse));
    sessionStorage.setItem(SessionKeys.Verifier, v);
    const authUrl = buildAuthUrl(
      oidcMetadataResponse.authEndpoint,
      oidcMetadataResponse.clientId,
      challenge,
      email,
      true
    );
    window.location.replace(authUrl);
  };

  const signOut = () => {
    const oidcMeta = sessionStorage.getItem(SessionKeys.OidcMeta);
    const token = sessionStorage.getItem(SessionKeys.AmpIdToken);
    if (oidcMeta !== null && token !== null) {
      const oidcConfig = JSON.parse(oidcMeta) as OidcConfigStorage;
      const url = `${oidcConfig.end_session_endpoint}?id_token_hint=${token}&post_logout_redirect_uri=${redirectUri}`;
      window.location.replace(url);
    }
    sessionStorage.clear();
  };

  const getToken = async (force: boolean): Promise<AmpAccessToken> => {
    const expiration = new Date(mustGetSessionStorage(SessionKeys.AmpTokenExpiration));

    if (!force && expiration > new Date()) {
      return {
        token: mustGetSessionStorage(SessionKeys.AmpToken),
        expiration,
      };
    }

    const authStorage = mustGetSessionStorage(SessionKeys.Auth);
    const auth: AuthStorage = JSON.parse(authStorage) as AuthStorage;
    const oidcMeta = mustGetSessionStorage(SessionKeys.OidcMeta);
    const oidcConfig = JSON.parse(oidcMeta) as OidcConfigStorage;
    const refreshToken = mustGetSessionStorage(SessionKeys.AmpRefreshToken);

    const newToken = await getTokenByRefresh(oidcConfig.token_endpoint, auth.clientId, refreshToken);
    return newToken;
  };

  return {
    getInitialAuthState,
    signIn,
    signInIdp,
    signOut,
    getToken,
  };
};
